import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v1Tasks, type V1Admin } from '../../../../lib/v1';
import { familyAccountTasks } from '../../../../lib/familyAccountData';
import { AssigneeSelect } from '../../PbDashboard/AssigneeSelect';
import { TaskStatusSelect } from '../../PbDashboard/TaskStatus';
import { TaskPriorityToggle } from '../../PbDashboard/TaskPriority';
import { toLocalInput } from './helpers';
import type { TaskRow, PersonOption } from './types';
import { DateTimeLocalInput } from '../../../../components/DateTimeInput';

export interface TaskFormProps {
  dark: boolean;
  userId: number;
  initial?: TaskRow | null;
  onSaved: (task: TaskRow) => void;
  onCancel: () => void;
  /** Batch-12: Admin-Liste für die Bearbeiter-Auswahl (wie im Kundendashboard). */
  admins?: V1Admin[];
  /**
   * Batch-13 Paket E — Familienkonto-Zentrierung: wenn gesetzt, speichert das
   * Formular über die Konto-Endpoints (POST/PATCH /family-accounts/{accountId}
   * bzw. /tasks/{id}) statt personenbezogen über v1 (`userId` wird dann nur
   * noch als Default-Auswahl für die Personen-Zuordnung genutzt).
   */
  accountId?: number | null;
  /** Mitglieder des Kontos für die optionale Personen-Auswahl. */
  members?: PersonOption[];
}

export function TaskForm({ dark, userId, initial, onSaved, onCancel, admins, accountId, members }: TaskFormProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [titel, setTitel] = useState(initial?.titel ?? '');
  const [beschreibung, setBeschreibung] = useState(initial?.beschreibung ?? '');
  // T1: Deadline als Timestamp (datetime-local).
  const [zieldatum, setZieldatum] = useState(toLocalInput(initial?.zieldatum));
  // Batch-12: Bearbeiter-Zuweisung (Admin-IDs) wie im Kundendashboard.
  // Nur eine zuständige Person: Bestandsaufgaben mit mehreren behalten beim
  // Bearbeiten die erste.
  const [bearbeiter, setBearbeiter] = useState<number[]>(
    Array.isArray(initial?.bearbeiter) ? (initial!.bearbeiter as number[]).slice(0, 1) : [],
  );
  // Batch-13 Paket E — Personen-Zuordnung: im Konto-Modus per Default KEINE
  // Person ausgewählt (Konto-weiter Eintrag), außer beim Bearbeiten einer
  // Zeile mit vorhandenem user_id.
  const [personId, setPersonId] = useState<number | ''>(
    accountId != null ? (initial?.user_id ?? '') : '',
  );
  // Status (Offen / In Bearbeitung / Erledigt) — nur beim Bearbeiten wählbar.
  const [status, setStatus] = useState<number>(initial?.status ?? 0);
  // Priorität: 0 = Normal, 1 = Hoch (Alarm-Rot).
  const [priority, setPriority] = useState<number>(Number(initial?.priority ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titelRef = useRef<HTMLInputElement>(null);

  useEffect(() => { titelRef.current?.focus(); }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) { setError(tr('pflege.detail.dashboard.task_titel_required')); return; }
    if (!beschreibung.trim()) { setError(tr('pflege.detail.dashboard.task_beschr_required')); return; }
    if (!zieldatum) { setError(tr('pflege.detail.dashboard.task_date_required')); return; }
    setSaving(true);
    setError(null);
    try {
      let saved: TaskRow;
      if (accountId != null) {
        const payload = {
          titel: titel.trim(),
          beschreibung: beschreibung.trim(),
          zieldatum: zieldatum || null,
          bearbeiter: bearbeiter.length ? bearbeiter : null,
          user_id: personId === '' ? null : personId,
        };
        saved = initial?.id
          ? await familyAccountTasks.update(initial.id, { ...payload, status, priority }) as TaskRow
          : await familyAccountTasks.create(accountId, { ...payload, priority, status: 0 }) as TaskRow;
      } else if (initial?.id) {
        saved = await v1Tasks.update(initial.id, {
          titel: titel.trim(),
          beschreibung: beschreibung.trim(),
          status,
          priority,
          zieldatum: zieldatum || null,
          bearbeiter: bearbeiter.length ? bearbeiter : null,
        }) as TaskRow;
      } else {
        saved = await v1Tasks.create({
          user_id: userId,
          titel: titel.trim(),
          beschreibung: beschreibung.trim(),
          priority,
          status: 0,
          zieldatum: zieldatum || null,
          bearbeiter: bearbeiter.length ? bearbeiter : null,
        }) as TaskRow;
      }
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('pflege.detail.dashboard.save_error'));
    } finally {
      setSaving(false);
    }
  }, [titel, beschreibung, zieldatum, bearbeiter, status, priority, personId, accountId, userId, initial, tr, onSaved]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '7px 10px',
    border: `1px solid ${t.border}`, borderRadius: 7,
    background: t.inputBg, color: t.text, outline: 'none',
    fontFamily: 'Outfit,sans-serif', width: '100%', boxSizing: 'border-box',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${t.border}`, background: t.hover }}>
      <input ref={titelRef} type="text" placeholder={tr('pflege.detail.dashboard.task_titel_placeholder')} value={titel} onChange={e => setTitel(e.target.value)} style={inputStyle} />
      <input type="text" placeholder={tr('pflege.detail.dashboard.task_beschr_placeholder')} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} style={inputStyle} />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: t.muted }}>
        {tr('pflege.detail.dashboard.task_date_label')} *
        <DateTimeLocalInput ariaLabel={tr('pflege.detail.dashboard.task_date_label')} value={zieldatum} onChange={setZieldatum} style={{ ...inputStyle, color: zieldatum ? t.text : t.muted }} />
      </label>
      {/* Batch-12: Bearbeiter-Auswahl (Typeahead + Chips) wie im Kundendashboard */}
      <AssigneeSelect dark={dark} admins={admins ?? []} value={bearbeiter} onChange={setBearbeiter} compact />

      {/* Batch-13 Paket E — Personen-Zuordnung (nur im Konto-Modus): optional,
          Default "Konto" (kein Personenbezug). */}
      {accountId != null && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: t.muted }}>
          {tr('pflege.tasks.person_label')}
          <select
            aria-label={tr('pflege.tasks.person_label')}
            value={personId}
            onChange={e => setPersonId(e.target.value === '' ? '' : Number(e.target.value))}
            style={inputStyle}
          >
            <option value="">{tr('pflege.tasks.person_none')}</option>
            {(members ?? []).map(m => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </select>
        </label>
      )}

      <TaskPriorityToggle dark={dark} value={priority} onChange={setPriority} compact />

      {initial?.id != null && (
        <TaskStatusSelect dark={dark} value={status} onChange={setStatus} compact />
      )}

      {error && <div style={{ fontSize: 11.5, color: '#b42318' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : (initial?.id ? tr('common.action.save') : tr('pflege.detail.dashboard.hinzufuegen'))}
        </button>
        <button type="button" onClick={onCancel} style={{ fontSize: 13, padding: '7px 14px', background: 'none', border: `1px solid ${t.border}`, borderRadius: 7, color: t.muted, cursor: 'pointer' }}>
          {tr('common.action.cancel')}
        </button>
      </div>
    </form>
  );
}
